// services/notification.service.ts
import { Injectable, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { HttpClient } from '@angular/common/http';
import { NotificationBellService } from "./notification-bell.service";
import { SystemApp } from "../../constans/SystemConfigurations";
import { ApiUrl } from "../../shared/models/apiurl.models";
import {retryWhen, delay, takeUntil, delayWhen, tap} from 'rxjs/operators';
import { Subject, timer, EMPTY } from 'rxjs';
import { DOCUMENT } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
    private socket$: WebSocketSubject<any>;
    private destroy$ = new Subject<void>();
    private retryInterval = 3000; // Start with 3 seconds
    private maxRetryInterval = 30000; // Max 30 seconds between retries
    private isVisible = true;

    constructor(
        private http: HttpClient,
        private notificationBellService: NotificationBellService,
        private apiUrl: ApiUrl,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        this.initVisibilityHandler();
    }

    connect(): void {
        if (this.socket$ && !this.socket$.closed) return;

        const wsUrl = `${SystemApp.web_socket}notifications/?token=${this.apiUrl.getJWTTokenKey()}`;

        this.socket$ = webSocket({
            url: wsUrl,
            openObserver: {
                next: () => {
                    this.retryInterval = 3000; // Reset retry interval on successful connection
                }
            }
        });

        this.socket$.pipe(
            retryWhen(errors => errors.pipe(
                delayWhen(() => timer(this.retryInterval)),
                tap(() => {
                    this.retryInterval = Math.min(this.retryInterval * 2, this.maxRetryInterval);
                })
            )),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (msg) => this.handleMessage(msg),
            error: (err) => this.handleError(err),
            complete: () => this.handleComplete()
        });
    }

    private handleMessage(msg: any): void {
        if (msg.type === 'new_notification') {
            this.notificationBellService.notifyNotifications();
        }
    }

    private handleError(err: Error): void {
        console.error('WebSocket error:', err);
    }

    private handleComplete(): void {
        console.log('WebSocket connection closed');
        if (this.isVisible) {
            this.connect(); // Reconnect if closed unexpectedly
        }
    }

    private initVisibilityHandler(): void {
        if (typeof this.document !== 'undefined') {
            this.document.addEventListener('visibilitychange', () => {
                this.isVisible = !this.document.hidden;
                if (this.isVisible && (this.socket$?.closed || !this.socket$)) {
                    this.connect();
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.socket$) {
            this.socket$.complete();
        }
    }
}
